import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, CloudRain, Wind, Flame, Compass, Eclipse, Infinity, MessageCircle, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '../components/SEO';
import { useLang } from '../context/LangContext';

const categories = [
  {
    id: 'grief',
    icon: CloudRain,
    titleKey: 'guidance.grief',
    descKey: 'guidance.griefDesc',
    patterns: [1, 7],
    exerciseKey: 'guidance.griefExercise',
    exerciseTitleKey: 'guidance.griefExerciseTitle',
    askPrompt: "I'm going through grief and loss. Can you help me understand this pain?",
    askPromptCn: '我正在经历悲伤和失去。你能帮我理解这种痛苦吗？',
  },
  {
    id: 'loneliness',
    icon: Wind,
    titleKey: 'guidance.loneliness',
    descKey: 'guidance.lonelinessDesc',
    patterns: [3, 6],
    exerciseKey: 'guidance.lonelinessExercise',
    exerciseTitleKey: 'guidance.lonelinessExerciseTitle',
    askPrompt: "I feel deeply lonely even around people. What's wrong with me?",
    askPromptCn: '我即使在人群中也感到深深的孤独。我怎么了？',
  },
  {
    id: 'anxiety',
    icon: Flame,
    titleKey: 'guidance.anxiety',
    descKey: 'guidance.anxietyDesc',
    patterns: [4, 5],
    exerciseKey: 'guidance.anxietyExercise',
    exerciseTitleKey: 'guidance.anxietyExerciseTitle',
    askPrompt: "I'm overwhelmed by anxiety and can't quiet my mind. How do I find peace?",
    askPromptCn: '我被焦虑压得喘不过气，无法让心灵安静。我如何找到平静？',
  },
  {
    id: 'faith',
    icon: Eclipse,
    titleKey: 'guidance.faith',
    descKey: 'guidance.faithDesc',
    patterns: [1, 2],
    exerciseKey: 'guidance.faithExercise',
    exerciseTitleKey: 'guidance.faithExerciseTitle',
    askPrompt: "I've lost my faith but still feel something. Is there a path between belief and nothing?",
    askPromptCn: '我失去了信仰但仍然感觉到什么。在信仰和虚无之间有没有一条路？',
  },
  {
    id: 'relationships',
    icon: Heart,
    titleKey: 'guidance.relationships',
    descKey: 'guidance.relationshipsDesc',
    patterns: [3, 7],
    exerciseKey: 'guidance.relationshipsExercise',
    exerciseTitleKey: 'guidance.relationshipsExerciseTitle',
    askPrompt: "My relationship is causing me deep pain. How do I navigate love and suffering?",
    askPromptCn: '我的关系让我深感痛苦。我如何在爱与苦之间找到方向？',
  },
  {
    id: 'purpose',
    icon: Compass,
    titleKey: 'guidance.purpose',
    descKey: 'guidance.purposeDesc',
    patterns: [2, 6],
    exerciseKey: 'guidance.purposeExercise',
    exerciseTitleKey: 'guidance.purposeExerciseTitle',
    askPrompt: "I feel empty and purposeless despite having everything. What's the point?",
    askPromptCn: '尽管拥有一切，我仍感到空虚和没有目标。活着的意义是什么？',
  },
  {
    id: 'existential',
    icon: Infinity,
    titleKey: 'guidance.existential',
    descKey: 'guidance.existentialDesc',
    patterns: [1, 5, 6],
    exerciseKey: 'guidance.existentialExercise',
    exerciseTitleKey: 'guidance.existentialExerciseTitle',
    askPrompt: "Nothing feels real or meaningful anymore. How do I find solid ground?",
    askPromptCn: '一切都不再真实或有意义。我如何找到坚实的立足点？',
  },
];

const patternNames = {
  1: { en: 'Source Beyond Comprehension', cn: '超越理解的源头' },
  2: { en: 'Consciousness Is Fundamental', cn: '意识是根本' },
  3: { en: 'Connection Over Separation', cn: '连接胜于分离' },
  4: { en: 'Ego Is the Root of Suffering', cn: '自我是痛苦之根' },
  5: { en: 'Letting Go Is Freedom', cn: '放下即自由' },
  6: { en: 'The Present Moment', cn: '当下此刻' },
  7: { en: 'What Remains Is Love', cn: '留下的是爱' },
};

export default function Guidance() {
  const [expanded, setExpanded] = useState(null);
  const { lang, t } = useLang();

  const toggle = (id) => setExpanded(expanded === id ? null : id);

  return (
    <div className="min-h-screen pt-24 pb-20 px-6">
      <SEO path="/guidance" />
      <div className="max-w-2xl mx-auto">
        <h1 className="font-serif text-3xl md:text-4xl text-warm text-center mb-4">
          {t('guidance.title')}
        </h1>
        <p className="text-warm-100/50 text-center mb-4 max-w-lg mx-auto">
          {t('guidance.subtitle')}
        </p>
        <p className="text-warm-100/35 text-xs text-center mb-16 max-w-md mx-auto italic">
          {t('guidance.disclaimer')}
        </p>

        <div className="space-y-4">
          {categories.map((cat) => {
            const Icon = cat.icon;
            const isOpen = expanded === cat.id;
            const askUrl = `/ask?q=${encodeURIComponent(lang === 'cn' ? cat.askPromptCn : cat.askPrompt)}`;

            return (
              <div
                key={cat.id}
                className={`border rounded-xl transition-all duration-300 ${
                  isOpen
                    ? 'border-gold/30 bg-dark-100/50'
                    : 'border-dark-200/40 hover:border-dark-200/60'
                }`}
              >
                {/* Header */}
                <button
                  onClick={() => toggle(cat.id)}
                  className="w-full flex items-center gap-4 px-6 py-5 text-left"
                >
                  <Icon
                    size={20}
                    className={`shrink-0 transition-colors ${
                      isOpen ? 'text-gold' : 'text-warm-100/40'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <h2 className={`font-serif text-lg transition-colors ${
                      isOpen ? 'text-warm' : 'text-warm-100/70'
                    }`}>
                      {t(cat.titleKey)}
                    </h2>
                    <p className="text-warm-100/40 text-sm mt-0.5 line-clamp-1">
                      {t(cat.descKey)}
                    </p>
                  </div>
                  {isOpen ? (
                    <ChevronUp size={16} className="text-gold/50 shrink-0" />
                  ) : (
                    <ChevronDown size={16} className="text-warm-100/30 shrink-0" />
                  )}
                </button>

                {/* Expanded content */}
                {isOpen && (
                  <div className="px-6 pb-6 animate-fade-in">
                    <div className="border-t border-dark-200/30 pt-5">
                      {/* Description */}
                      <p className="text-warm-100/60 text-sm leading-relaxed mb-5">
                        {t(cat.descKey)}
                      </p>

                      {/* Related patterns */}
                      <div className="mb-5">
                        <p className="text-warm-100/40 text-xs uppercase tracking-wider mb-2">
                          {t('guidance.relatedPatterns')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {cat.patterns.map((num) => (
                            <Link
                              key={num}
                              to="/patterns"
                              className="text-xs px-3 py-1.5 rounded-full border border-gold/20 text-gold/60 hover:border-gold/40 hover:text-gold transition-colors"
                            >
                              {num}. {patternNames[num]?.[lang] || patternNames[num]?.en}
                            </Link>
                          ))}
                        </div>
                      </div>

                      {/* Exercise */}
                      <div className="bg-dark/50 border border-dark-200/30 rounded-lg p-5 mb-5">
                        <p className="text-gold/70 text-xs uppercase tracking-wider mb-2">
                          {t('guidance.tryThis')}
                        </p>
                        <h3 className="font-serif text-warm text-sm mb-2">
                          {t(cat.exerciseTitleKey)}
                        </h3>
                        <p className="text-warm-100/55 text-sm leading-relaxed whitespace-pre-line">
                          {t(cat.exerciseKey)}
                        </p>
                      </div>

                      {/* CTA */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Link
                          to={askUrl}
                          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm transition-colors bg-gold/10 border border-gold/25 text-gold hover:bg-gold/20"
                        >
                          <MessageCircle size={14} />
                          {t('guidance.talkToAI')}
                        </Link>
                        <Link
                          to="/practice"
                          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm transition-colors border border-dark-200/40 text-warm-100/50 hover:text-warm hover:border-dark-200/60"
                        >
                          {t('guidance.tryPractice')}
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom message */}
        <div className="mt-16 text-center">
          <p className="text-warm-100/35 text-sm font-serif italic leading-relaxed max-w-md mx-auto">
            {t('guidance.closing')}
          </p>
        </div>
      </div>
    </div>
  );
}
